import { useRef, useEffect, RefObject } from "react";

/**
 * A custom React hook to detect clicks outside of a referenced component.
 * @param {() => void} callback The function to call when an outside click is detected.
 * @returns {RefObject<any>} A ref object to be attached to the component you want to monitor.
 */
export const useOutsideClick = <T extends HTMLElement>(callback: () => void): RefObject<T> => {
  const ref = useRef<T>(null);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        callback();
      }
    };

    document.addEventListener("click", handleClick);

    return () => {
      document.removeEventListener("click", handleClick);
    };
  }, [ref, callback]);

  return ref;
};
